import { Check, ListPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useCartActions } from '@/features/cart/useCart';
import type { PricedVariant, Product } from '@/types/catalog';

import { Button, type ButtonProps } from '../ui/button';

// `variant` é omitido de ButtonProps: aqui significa o tamanho do produto, e a
// cor do botão é decidida internamente pelo estado de "adicionado".
interface AddToCartButtonProps extends Omit<ButtonProps, 'onClick' | 'children' | 'variant'> {
  product: Product;
  variant: PricedVariant;
  quantity?: number;
  /** Só o ícone, para grades densas. */
  compact?: boolean;
  label?: string;
}

/**
 * Adiciona uma variante ao carrinho com uma confirmação curta.
 *
 * A gaveta de propósito não abre sozinha: numa página de catálogo a maioria
 * adiciona vários itens seguidos, e um painel que rouba o foco a cada clique
 * transforma um pedido grande numa briga com a interface.
 */
export function AddToCartButton({
  product,
  variant,
  quantity = 1,
  compact = false,
  label = 'Adicionar à lista',
  ...buttonProps
}: AddToCartButtonProps) {
  const { addItem, openCart } = useCartActions();
  const [justAdded, setJustAdded] = useState(false);
  const timerRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const soldOut = variant.stock === 'out-of-stock';

  function handleAdd() {
    const result = addItem(variant.sku, quantity);

    if (result.status === 'unavailable') {
      toast.error('Produto sob encomenda', {
        description: `${product.name} — ${variant.label}. Fale com a gente para saber a previsão.`,
      });
      return;
    }

    if (result.status === 'unknown-sku' || result.addedQuantity === 0) {
      // Já está no limite por item: comemorar "Adicionado!" seria mentira.
      toast.warning('Limite atingido', {
        description: `Você já tem o máximo de ${result.resultingQuantity} unidades deste item na lista.`,
        action: { label: 'Ver lista', onClick: openCart },
      });
      return;
    }

    setJustAdded(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setJustAdded(false), 1600);

    if (result.status === 'clamped') {
      toast.warning('Adicionamos o máximo possível', {
        description: `${result.addedQuantity} de ${quantity} unidades. O limite é ${result.resultingQuantity} por item.`,
        action: { label: 'Ver lista', onClick: openCart },
      });
      return;
    }

    toast.success('Adicionado à lista!', {
      description: `${product.name} — ${variant.label}`,
      action: { label: 'Ver lista', onClick: openCart },
    });
  }

  if (soldOut) {
    return (
      <Button variant="outline" disabled {...buttonProps}>
        <ListPlus aria-hidden="true" />
        {!compact && 'Sob encomenda'}
      </Button>
    );
  }

  return (
    <Button
      onClick={handleAdd}
      variant={justAdded ? 'whatsapp' : 'primary'}
      aria-label={compact ? `Adicionar ${product.name} à lista` : undefined}
      {...buttonProps}
    >
      {justAdded ? <Check aria-hidden="true" /> : <ListPlus aria-hidden="true" />}
      {!compact && (justAdded ? 'Adicionado!' : label)}
    </Button>
  );
}
